/**
 * Detalle del episodio: una atención firmada, en solo lectura.
 *
 * El registro original no se edita nunca. Lo que sí puede añadirse es una
 * NOTA DE ENMIENDA: una corrección posterior, con autor y fecha, que se muestra
 * debajo sin tapar lo que se registró en su momento. Es lo que exige la norma
 * de historia clínica y también lo único honesto: quien lea la historia dentro
 * de un año tiene que poder ver el error y su corrección.
 */
import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FilePen, FileSignature, Plus, Stethoscope } from "lucide-react";
import { crearEnmienda, getEpisodio } from "../api/clinical";
import { formatApiError } from "../api/client";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";
import { RecetaEpisodio } from "../components/atencion/RecetaEpisodio";
import { OrdenLaboratorioEpisodio } from "../components/atencion/OrdenLaboratorio";
import { PlanTratamientoEpisodio } from "../components/atencion/PlanTratamiento";
import { useAuth } from "../auth/AuthProvider";
import { useZonaHoraria, useBase } from "../auth/TenantBrandingProvider";
import { fechaHora } from "../lib/fecha";

export function EpisodioDetailPage() {
  const tz = useZonaHoraria();
  const { me } = useAuth();
  const [enmendarOpen, setEnmendarOpen] = useState(false);
  // Se desbloquea a mano y se vuelve a bloquear al salir de la página: si
  // quedara abierto, el siguiente que entre a esta atención firmada la
  // encontraría editable sin haberlo pedido.
  const [agregarACerrada, setAgregarACerrada] = useState(false);
  const puedeEnmendar = me?.is_super_admin || (me?.permisos.includes("hce:write") ?? false);
  const puedeEditar = puedeEnmendar;
  const { id } = useParams<{ slug: string; id: string }>();
  const base = useBase();
  const episodioId = id ? Number(id) : NaN;

  const { data: ep, isLoading, error } = useQuery({
    queryKey: ["episodio", episodioId],
    queryFn: () => getEpisodio(episodioId),
    enabled: Number.isFinite(episodioId),
  });

  return (
    <div className="space-y-6">
      <Link
        to={ep ? `${base}/pacientes/${ep.paciente_id}` : `${base}/pacientes`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver al paciente
      </Link>

      {isLoading && <div className="text-sm text-slate-400">Cargando…</div>}
      {error && <Alert>No se pudo cargar la atención.</Alert>}

      {ep && (
        <>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)] flex items-center justify-center">
                <Stethoscope className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  Atención del {fechaHora(ep.inicio, tz)}
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  <span className="font-medium text-slate-900">{ep.paciente_nombre}</span>
                  {" "}· atendido por {ep.profesional_nombre}
                  {" "}<span className="font-mono">({ep.profesional_colegiatura})</span>
                  {ep.especialidad_nombre && ` · ${ep.especialidad_nombre}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {ep.estado === "CERRADO" ? (
                <Badge variant="success">
                  <FileSignature className="w-3 h-3 inline mr-1" />
                  Cerrado
                </Badge>
              ) : ep.estado === "ABIERTO" ? (
                <Badge variant="warning">Borrador</Badge>
              ) : (
                <Badge variant="danger">Anulado</Badge>
              )}
            </div>
          </div>

          {ep.estado === "ABIERTO" && (
            <Alert variant="info">
              Esta atención está como borrador — todavía no firmada. Vuelve a "Atender" para
              completarla y cerrarla.
            </Alert>
          )}

          {ep.motivo_consulta && (
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Motivo de consulta</h2>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{ep.motivo_consulta}</p>
              </CardBody>
            </Card>
          )}

          {ep.anamnesis && (
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Anamnesis</h2>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{ep.anamnesis}</p>
              </CardBody>
            </Card>
          )}

          {ep.examen_fisico && (
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Examen físico</h2>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{ep.examen_fisico}</p>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader>
              <h2 className="font-semibold text-slate-900">Diagnósticos</h2>
            </CardHeader>
            <CardBody>
              {ep.diagnosticos.length === 0 ? (
                <p className="text-sm text-slate-400">Sin diagnósticos registrados.</p>
              ) : (
                <div className="space-y-2">
                  {ep.diagnosticos.map((d) => (
                    <div
                      key={d.id}
                      className="flex items-center gap-3 p-2 border border-slate-100 rounded"
                    >
                      <span className="font-mono text-xs text-slate-700 w-20">{d.cie10_codigo}</span>
                      <span className="flex-1 text-sm text-slate-900">{d.cie10_descripcion}</span>
                      <Badge variant={d.tipo === "PRINCIPAL" ? "success" : "neutral"}>
                        {d.tipo === "PRINCIPAL" ? "Principal" : "Secundario"}
                      </Badge>
                      <Badge>
                        {d.condicion === "DEFINITIVO" ? "Definitivo" : "Presuntivo"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {ep.plan && (
            <Card>
              <CardHeader>
                <h2 className="font-semibold text-slate-900">Indicaciones</h2>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{ep.plan}</p>
              </CardBody>
            </Card>
          )}

          {/* Lo que el paciente se lleva, en el orden en que sale de la
              consulta: la receta a farmacia, los exámenes a caja y laboratorio,
              y los controles a la agenda.

              Firmada la atención, los tres quedan en solo lectura. Lo que ya
              existe se sigue viendo e imprimiendo —la receta emitida no
              desaparece porque se firme—, pero no nacen cosas nuevas sin
              decirlo a propósito con el botón de abajo. */}
          <RecetaEpisodio
            episodioId={ep.id}
            cerrada={ep.estado === "CERRADO"}
            permitirAgregar={agregarACerrada}
          />

          <OrdenLaboratorioEpisodio
            episodioId={ep.id}
            cerrada={ep.estado === "CERRADO"}
            permitirAgregar={agregarACerrada}
          />

          <PlanTratamientoEpisodio
            episodioId={ep.id}
            cerrada={ep.estado === "CERRADO"}
            permitirAgregar={agregarACerrada}
          />

          {/* La salida para el olvido de siempre: firmar y caer en que faltaba
              la receta.

              No reabre la atención —el diagnóstico, la anamnesis y el examen
              siguen intactos, y para corregir eso está la enmienda—: solo
              desbloquea emitir, y lo que se emita queda marcado como posterior
              a la firma. Añadir es aditivo; reabrir sería destructivo. */}
          {ep.estado === "CERRADO" && !agregarACerrada && puedeEditar && (
            <Card>
              <CardBody className="flex flex-wrap items-center justify-between gap-3">
                <div className="max-w-xl">
                  <p className="text-sm font-medium text-slate-900">
                    ¿Faltó una receta o unos exámenes?
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    La atención ya está firmada y no se reabre. Puedes emitir lo
                    que falte, y quedará registrado como añadido después.
                  </p>
                </div>
                <Button variant="secondary" onClick={() => setAgregarACerrada(true)}>
                  <Plus className="h-4 w-4" />
                  Añadir a esta atención
                </Button>
              </CardBody>
            </Card>
          )}

          {ep.estado === "CERRADO" && ep.firmado_en && (
            <Card>
              <CardBody>
                <div className="flex items-start gap-3 text-sm">
                  <FileSignature className="w-5 h-5 text-[color:var(--color-link)] mt-0.5" />
                  <div className="text-slate-700">
                    <p className="font-medium">Atención firmada</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Cerrada el {fechaHora(ep.firmado_en, tz)} por{" "}
                      {ep.profesional_nombre}. Este registro es inmutable.
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {ep.estado === "CERRADO" && (
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h2 className="flex items-center gap-2 font-semibold text-slate-900">
                      <FilePen className="h-4 w-4 text-amber-600" aria-hidden="true" />
                      Notas de enmienda
                      {ep.enmiendas.length > 0 && (
                        <Badge variant="warning">{ep.enmiendas.length}</Badge>
                      )}
                    </h2>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Correcciones posteriores a la firma. No modifican lo registrado
                      arriba: se añaden debajo, con autor y fecha.
                    </p>
                  </div>
                  {puedeEnmendar && (
                    <Button variant="ghost" onClick={() => setEnmendarOpen(true)}>
                      <FilePen className="h-4 w-4" />
                      Agregar enmienda
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardBody className={ep.enmiendas.length === 0 ? "" : "space-y-3"}>
                {ep.enmiendas.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    Sin enmiendas. La atención se mantiene tal como se firmó.
                  </p>
                ) : (
                  ep.enmiendas.map((e) => (
                    <div
                      key={e.id}
                      className="rounded-lg border-l-4 border-amber-300 bg-amber-50/50 px-4 py-3"
                    >
                      <p className="text-sm font-medium text-slate-800">{e.motivo}</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
                        {e.texto}
                      </p>
                      <p className="mt-2 text-xs text-slate-500">
                        {e.profesional_nombre} ·{" "}
                        <span className="font-mono">{e.profesional_colegiatura}</span> ·{" "}
                        {fechaHora(e.created_at, tz)}
                      </p>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          )}

          <EnmiendaDialog
            episodioId={episodioId}
            open={enmendarOpen}
            onClose={() => setEnmendarOpen(false)}
          />
        </>
      )}
    </div>
  );
}


function EnmiendaDialog({
  episodioId,
  open,
  onClose,
}: {
  episodioId: number;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [motivo, setMotivo] = useState("");
  const [texto, setTexto] = useState("");
  const [error, setError] = useState<string | null>(null);

  const crear = useMutation({
    mutationFn: () => crearEnmienda(episodioId, { motivo: motivo.trim(), texto: texto.trim() }),
    onSuccess: (ep) => {
      queryClient.setQueryData(["episodio", episodioId], ep);
      setMotivo("");
      setTexto("");
      setError(null);
      onClose();
    },
    onError: (e) => setError(formatApiError(e, "No se pudo registrar la enmienda.")),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    crear.mutate();
  }

  const valido = motivo.trim().length >= 3 && texto.trim().length >= 10;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Agregar nota de enmienda"
      description="Queda firmada con tu nombre y colegiatura, y no se puede editar ni borrar después."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <Alert variant="info">
          Lo registrado en la atención no se modifica. Esta nota se añade debajo para
          que la historia muestre qué se escribió y qué se corrigió.
        </Alert>

        <Field label="Motivo" htmlFor="en_mot" hint="En pocas palabras, qué se corrige.">
          <Input
            id="en_mot"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={120}
            placeholder="Ej. Diagnóstico mal codificado"
          />
        </Field>

        <Field
          label="Corrección"
          htmlFor="en_txt"
          hint="Qué decía y qué debe decir. Mínimo 10 caracteres."
        >
          <textarea
            id="en_txt"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={5}
            maxLength={4000}
            placeholder="Ej. Se consignó R51 (cefalea). El diagnóstico correcto es G43.9 (migraña sin especificar), confirmado en el control del 12/08."
            className="w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
          />
        </Field>

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={crear.isPending} disabled={!valido}>
            Firmar enmienda
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
